import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Shield, Info, ExternalLink } from 'lucide-react'
import { useI18n } from '../i18n'

/**
 * TwoFactorGuideModal — Bottom-sheet popup explaining how 2FA setup works.
 * 
 * Triggered from a "What is two-factor authentication?" link on the 
 * TwoFactorSetup choose-method screen and TwoFactorMigrationGate.
 * 
 * v148.1.0: Initial implementation
 * 
 * @param {boolean} isOpen - Controls visibility
 * @param {function} onClose - Called when modal should close
 * @param {'blue'|'navy'} theme - Color theme (blue for patient, navy for MD)
 */
export default function TwoFactorGuideModal({ isOpen, onClose, theme = 'blue' }) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)
  const [animating, setAnimating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setMounted(true)
      requestAnimationFrame(() => setAnimating(true))
      document.body.style.overflow = 'hidden'
    } else {
      setAnimating(false)
      const timer = setTimeout(() => {
        setMounted(false)
        document.body.style.overflow = ''
      }, 300)
      return () => clearTimeout(timer)
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  if (!mounted) return null

  const isNavy = theme === 'navy'
  const accentBg = isNavy ? 'bg-[#15354F]' : 'bg-orella-500'
  const accentText = isNavy ? 'text-[#15354F]' : 'text-orella-500'
  const pillBg = isNavy ? 'bg-[#15354F]/[0.08] hover:bg-[#15354F]/[0.16]' : 'bg-orella-50 hover:bg-orella-100'
  const calloutBg = isNavy ? 'bg-[#15354F]/[0.06]' : 'bg-orella-50'
  const activeBoxBorder = isNavy ? 'border-[#15354F] bg-[#15354F]/[0.06]' : 'border-orella-500 bg-orella-50'

  const steps = [
    {
      title: t('twoFactor.pickYourMethod'),
      desc: (
        <>Choose between an <strong className="text-[#15354F]">authenticator app</strong> (recommended) or <strong className="text-[#15354F]">{t('twoFactor.smsTextCodes')}</strong>.</>
      ),
    },
    {
      title: t('twoFactor.connectYourDevice'),
      desc: t('twoFactor.ifYouChoseAuthenticatorScan'),
      pills: true,
    },
    {
      title: t('twoFactor.enterYour6digitCode'),
      desc: t('twoFactor.typeTheCodeFromYour'),
      codeBoxes: true,
    },
    {
      title: t('twoFactor.saveYourBackupCodes'),
      desc: t('twoFactor.you'),
    },
  ]

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-end justify-center transition-opacity duration-300 ${animating ? 'opacity-100' : 'opacity-0'}`}
      style={{ backgroundColor: 'rgba(15,36,56,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Bottom sheet */}
      <div
        className={`w-full max-w-[420px] bg-white rounded-t-[20px] max-h-[85vh] overflow-y-auto bottom-actions [--ba-min:0px] transition-transform duration-300 ease-out ${animating ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3.5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center ${isNavy ? 'bg-[#15354F]' : 'bg-orella-50'}`}>
              <Shield size={16} className={isNavy ? 'text-white' : 'text-orella-500'} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#15354F]">{t('twoFactor.how2faSetupWorks')}</h2>
              <p className="text-[11px] text-gray-500">{t('twoFactor.justTakesAMinute')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X size={14} className="text-gray-500" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-5 pt-4 pb-6">
          {steps.map((s, i) => {
            const isFirst = i === 0
            const isLast = i === steps.length - 1

            return (
              <div key={i} className={`flex gap-3 ${isLast ? 'mb-4' : 'mb-[18px]'}`}>
                {/* Step indicator column */}
                <div className="flex-shrink-0 flex flex-col items-center">
                  <div
                    className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-bold ${
                      isFirst
                        ? `${accentBg} text-white`
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {i + 1}
                  </div>
                  {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                </div>

                {/* Content */}
                <div className={isLast ? '' : 'pb-1'}>
                  <p className="text-[13px] font-bold text-[#15354F]">{s.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{s.desc}</p>

                  {/* App download pills */}
                  {s.pills && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      <a
                        href="https://apps.apple.com/app/google-authenticator/id388497605"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-[10px] font-semibold text-[#15354F] px-2 py-[3px] rounded-full inline-flex items-center gap-1 transition-colors no-underline ${pillBg}`}
                      >
                        <ExternalLink size={9} className={accentText} />
                        {t('twoFactor.googleAuthenticator', 'Google Authenticator')}
                      </a>
                      <a
                        href="https://apps.apple.com/app/twilio-authy/id494168017"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-[10px] font-semibold text-[#15354F] px-2 py-[3px] rounded-full inline-flex items-center gap-1 transition-colors no-underline ${pillBg}`}
                      >
                        <ExternalLink size={9} className={accentText} />
                        {t('twoFactor.authy', 'Authy')}
                      </a>
                    </div>
                  )}

                  {/* Code input preview */}
                  {s.codeBoxes && (
                    <div className="flex gap-1 mt-2">
                      {['4', '7', '2', '', '', ''].map((digit, j) => (
                        <div
                          key={j}
                          className={`w-7 h-[34px] rounded-lg flex items-center justify-center text-base font-semibold text-[#15354F] ${
                            j === 3 ? `border-[1.5px] ${activeBoxBorder}` : 'border-[1.5px] border-gray-200'
                          }`}
                        >
                          {digit}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* Info callout */}
          <div className={`rounded-xl p-3 flex gap-2 items-start ${calloutBg}`}>
            <Shield size={14} className={`flex-shrink-0 mt-0.5 ${accentText}`} />
            <p className="text-[11.5px] text-[#15354F] leading-relaxed">
              <strong>{t('twoFactor.whyIsThisRequired')}</strong> {t('twoFactor.whyItMatters', 'Orella handles sensitive health information. Two-factor authentication adds a second layer of security beyond your password, keeping your data protected.')}
            </p>
          </div>

          {/* Got it button */}
          <button
            onClick={onClose}
            className={`w-full mt-4 py-3.5 text-white text-sm font-semibold rounded-full transition-colors ${
              isNavy ? 'bg-[#15354F] hover:bg-[#1e4568]' : 'bg-orella-500 hover:bg-orella-600'
            }`}
          >
            {t('describe.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
